import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import { Checkbox } from "@/components/ui/checkbox";
import LogoUpload from "@/components/LogoUpload";
import usePageTitle from "@/hooks/usePageTitle";

const SubmitApp = () => {
  // Set page title
  usePageTitle('Submit Your App');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    seoDescription: "",
    visitUrl: "",
    selectedCategories: [] as string[],
    selectedPlatforms: [] as string[],
    selectedSports: [] as string[],
    logoUrl: "",
    iosStoreUrl: "",
    androidStoreUrl: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  // Database data
  const [dbCategories, setDbCategories] = useState<{id: string, name: string}[]>([]);
  const [dbPlatforms, setDbPlatforms] = useState<{id: string, name: string}[]>([]);
  const [dbSports, setDbSports] = useState<{id: string, name: string}[]>([]);

  // Check authentication status when component mounts
  useEffect(() => {
    checkAuthStatus();
    loadData();
    
    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const currentUserId = session?.user?.id;
        setUserId(currentUserId || null);
        setIsLoading(false);
        
        if (!currentUserId && event === 'SIGNED_OUT') {
          // Redirect to auth page if signed out
          navigate("/auth");
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const loadData = async () => {
    try {
      // Load categories, platforms, and sports from the database
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      const { data: platformsData, error: platformsError } = await supabase
        .from('platforms')
        .select('id, name')
        .order('name');

      const { data: sportsData, error: sportsError } = await supabase
        .from('sports')
        .select('id, name')
        .order('name');

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
        toast({
          title: "Error loading categories",
          description: categoriesError.message,
          variant: "destructive",
        });
      }
      
      if (platformsError) {
        console.error('Error loading platforms:', platformsError);
        toast({
          title: "Error loading platforms",
          description: platformsError.message,
          variant: "destructive",
        });
      }
      
      if (sportsError) {
        console.error('Error loading sports:', sportsError);
        toast({
          title: "Error loading sports",
          description: sportsError.message,
          variant: "destructive",
        });
      }

      if (categoriesData) setDbCategories(categoriesData);
      if (platformsData) setDbPlatforms(platformsData);
      if (sportsData) setDbSports(sportsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error loading data",
        description: "There was an error loading form data. Please try refreshing the page.",
        variant: "destructive",
      });
    }
  };

  const checkAuthStatus = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Redirect to auth page if not authenticated
        navigate("/auth");
        return;
      }
      setUserId(session.user.id);
      setIsLoading(false);
    } catch (error) {
      console.error('Error checking auth status:', error);
      toast({
        title: "Error checking authentication",
        description: "There was a problem verifying your login status. Please try logging in again.",
        variant: "destructive",
      });
      navigate("/auth"); // Redirect to auth on error
    }
  };

  // Updated function to generate a unique URL slug
  const generateUrlSlug = (name: string) => {
    // Create base slug from name
    const baseSlug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    // Add a random string to ensure uniqueness
    const randomString = Math.random().toString(36).substring(2, 6);
    return `${baseSlug}-${randomString}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.name || !formData.description || !formData.visitUrl) {
      toast({
        title: "Missing required fields",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.selectedCategories.length === 0 || 
        formData.selectedPlatforms.length === 0 || 
        formData.selectedSports.length === 0) {
      toast({
        title: "Missing selections",
        description: "Please select at least one category, platform, and sport.",
        variant: "destructive",
      });
      return;
    }
    
    // Double-check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit your app.",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Starting app submission process...");
      const urlSlug = generateUrlSlug(formData.name);
      
      // Insert the app with user_id field to track ownership
      console.log("Inserting app into database...");
      const { data: app, error: appError } = await supabase.from('apps').insert({
        name: formData.name,
        description: formData.description,
        seo_description: formData.seoDescription || formData.description,
        visit_url: formData.visitUrl,
        url_slug: urlSlug,
        logo_url: formData.logoUrl,
        ios_store_url: formData.iosStoreUrl,
        android_store_url: formData.androidStoreUrl,
        user_id: session.user.id // Add user_id to track ownership
      }).select('id').single();

      if (appError) {
        console.error('Error inserting app:', appError);
        throw appError;
      }

      console.log("App inserted successfully with ID:", app.id);
      const appId = app.id;
      
      // Insert categories (make sure to handle this one at a time to avoid duplicates)
      if (formData.selectedCategories.length > 0) {
        console.log("Inserting category relations...");
        
        // Insert categories one at a time to avoid potential conflicts
        for (const categoryId of formData.selectedCategories) {
          const { error } = await supabase
            .from('app_categories')
            .insert({
              app_id: appId,
              category_id: categoryId
            });
            
          if (error) {
            console.error(`Error inserting category ${categoryId}:`, error);
            // Continue with other categories even if one fails
          }
        }
      }
      
      // Insert platforms
      if (formData.selectedPlatforms.length > 0) {
        console.log("Inserting platform relations...");
        
        // Insert platforms one at a time
        for (const platformId of formData.selectedPlatforms) {
          const { error } = await supabase
            .from('app_platforms')
            .insert({
              app_id: appId,
              platform_id: platformId
            });
            
          if (error) {
            console.error(`Error inserting platform ${platformId}:`, error);
            // Continue with other platforms even if one fails
          }
        }
      }
      
      // Insert sports
      if (formData.selectedSports.length > 0) {
        console.log("Inserting sport relations...");
        
        // Insert sports one at a time
        for (const sportId of formData.selectedSports) {
          const { error } = await supabase
            .from('app_sports')
            .insert({
              app_id: appId,
              sport_id: sportId
            });
            
          if (error) {
            console.error(`Error inserting sport ${sportId}:`, error);
            // Continue with other sports even if one fails
          }
        }
      }

      console.log("All relations inserted successfully!");
      toast({
        title: "Thank you for your submission!",
        description: "We'll review your app and add it to our directory soon.",
      });
      
      navigate("/");
    } catch (error: any) {
      console.error('Error submitting app:', error);
      toast({
        title: "Error submitting app",
        description: error?.message || "There was an error submitting your app. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoUpload = (url: string) => {
    setFormData(prev => ({
      ...prev,
      logoUrl: url
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId]
    }));
  };

  const togglePlatform = (platformId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedPlatforms: prev.selectedPlatforms.includes(platformId)
        ? prev.selectedPlatforms.filter(id => id !== platformId)
        : [...prev.selectedPlatforms, platformId]
    }));
  };

  const toggleSport = (sportId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedSports: prev.selectedSports.includes(sportId)
        ? prev.selectedSports.filter(id => id !== sportId)
        : [...prev.selectedSports, sportId]
    }));
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <div className="container py-12 flex justify-center items-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div className="container py-8">
        <h1 className="text-2xl font-bold mb-6">Submit Your App</h1>
        <div className="max-w-2xl mx-auto bg-white rounded-lg border p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="name">App Name</label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid gap-2">
                <label htmlFor="description">
                  Description <span className="text-xs text-gray-500">(max 500 characters)</span>
                </label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  maxLength={500}
                  className="min-h-[120px]"
                  required
                />
                <div className="text-xs text-gray-500 text-right">
                  {formData.description.length}/500 characters
                </div>
              </div>

              <div className="grid gap-2">
                <label htmlFor="visitUrl">Website URL</label>
                <Input
                  id="visitUrl"
                  type="url"
                  value={formData.visitUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, visitUrl: e.target.value }))}
                  required
                />
              </div>

              <div className="grid gap-2">
                <label>App Logo</label>
                <LogoUpload onLogoUpload={handleLogoUpload} />
              </div>

              <div className="grid gap-2">
                <label className="font-medium">Categories</label>
                <div className="grid grid-cols-2 gap-2">
                  {dbCategories.map((category) => (
                    <div key={category.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`category-${category.id}`} 
                        checked={formData.selectedCategories.includes(category.id)}
                        onCheckedChange={() => toggleCategory(category.id)}
                      />
                      <label 
                        htmlFor={`category-${category.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {category.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-2">
                <label className="font-medium">Platforms</label>
                <div className="grid grid-cols-2 gap-2">
                  {dbPlatforms.map((platform) => (
                    <div key={platform.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`platform-${platform.id}`} 
                        checked={formData.selectedPlatforms.includes(platform.id)}
                        onCheckedChange={() => togglePlatform(platform.id)}
                      />
                      <label 
                        htmlFor={`platform-${platform.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {platform.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {formData.selectedPlatforms.some(id => 
                dbPlatforms.find(p => p.id === id)?.name === "iOS") && (
                <div className="grid gap-2">
                  <label htmlFor="iosStoreUrl">App Store URL (iOS)</label>
                  <Input
                    id="iosStoreUrl"
                    type="url"
                    placeholder="https://apps.apple.com/app/..."
                    value={formData.iosStoreUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, iosStoreUrl: e.target.value }))}
                  />
                </div>
              )}

              {formData.selectedPlatforms.some(id => 
                dbPlatforms.find(p => p.id === id)?.name === "Android") && (
                <div className="grid gap-2">
                  <label htmlFor="androidStoreUrl">Google Play URL (Android)</label>
                  <Input
                    id="androidStoreUrl"
                    type="url"
                    placeholder="https://play.google.com/store/apps/details?id=..."
                    value={formData.androidStoreUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, androidStoreUrl: e.target.value }))}
                  />
                </div>
              )}

              <div className="grid gap-2">
                <label className="font-medium">Sports</label>
                <div className="grid grid-cols-2 gap-2">
                  {dbSports.map((sport) => (
                    <div key={sport.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`sport-${sport.id}`} 
                        checked={formData.selectedSports.includes(sport.id)}
                        onCheckedChange={() => toggleSport(sport.id)}
                      />
                      <label 
                        htmlFor={`sport-${sport.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {sport.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit App"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubmitApp;
